import classNames from "classnames";
import Emojify from "~/components/shared/Emojify";
import MaybeLink from "~/components/build/Show/components/Waterfall/MaybeLink";
import Badge from "~/components/shared/Badge";
import { WaterfallRowType } from "~/components/build/Show/lib/queries/waterfall";
import { resolveJobUrl, resolveStepUrl } from "~/components/build/Show/lib/urlForView";

type TextFormatter = Record<WaterfallRowType["label"]["format"], (text: string) => React.ReactNode>;

const textFormatter: TextFormatter = {
  emojify: (text) => <Emojify title={text} text={text} />,
  raw: (text) => <span title={text}>{text}</span>,
  code: (text) => (
    <code title={text} className="monospace">
      {text}
    </code>
  ),
};

type WaterfallRowLabelProps = {
  data: WaterfallRowType;
  depth?: number;
  collapsable?: boolean;
};

function useUrlForRow(data: WaterfallRowType, params: { open: boolean }) {
  if (Features.BuildSidebar) {
    if (data.type === "trigger") {
      return resolveStepUrl(data.step_uuid, params);
    }

    if (data.type === "command") {
      if (data.job_uuid) {
        return resolveJobUrl(data.job_uuid, params);
      } else if (data.step_uuid) {
        return resolveStepUrl(data.step_uuid, params);
      }
    }

    return null;
  }

  return data.job_url;
}

export default function WaterfallRowLabel(props: WaterfallRowLabelProps) {
  const { depth = 0, collapsable } = props;
  const rowUrl = useUrlForRow(props.data, { open: !collapsable });

  return (
    <MaybeLink
      data-testid="waterfall-job-link"
      newTab={props.data.type === "trigger" && !Features.BuildSidebar}
      to={rowUrl}
      className={classNames(
        "-mt-[1px] border-y border-gray-400",
        "flex flex-1 flex-col text-inherit no-underline hover:text-inherit focus:text-inherit",
      )}
      style={{ marginLeft: `${depth}rem` }}
    >
      <div
        className={classNames(
          "box-content flex w-full items-center leading-normal",
          depth > 0 && "border-l border-gray-400 bg-white",
          Features.BuildSidebar ? "h-[37.5px]" : "h-[52px]",
          collapsable && "pl-4",
        )}
      >
        <span className="w-full max-w-full truncate px-[15px] font-medium" data-testid="row-label">
          {props.data.parallel_group_index && props.data.parallel_group_total && (
            <Badge outline={true} className="charcoal-500 mr-1">
              {props.data.parallel_group_index}/{props.data.parallel_group_total}
            </Badge>
          )}
          {textFormatter[props.data.label.format](props.data.label.text)}
        </span>
      </div>
    </MaybeLink>
  );
}
